import {
  Container,
  Icon,
  IconButton,
  Text,
  useConfirmDialog,
} from "@fluidattacks/design";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { InputMessage } from "../input-message";
import { useWebSocket } from "../use-web-socket";
import { Welcome } from "../welcome";
import { useSidebarCollapseStore } from "components/side-bar/hooks/use-sidebar-collapse-store";
import { Messages } from "pages/dashboard/mcp-chat/messages";
import { useChatStore } from "pages/dashboard/mcp-chat/use-chat-store";

const Chat: React.FC<{
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ isExpanded, setIsExpanded }): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { toggleIsOpen, messages } = useChatStore();
  const { handleResetConversation } = useWebSocket();
  const { isCollapsed } = useSidebarCollapseStore();
  const { ConfirmDialog: ConfirmClearHistory, confirm } = useConfirmDialog();

  const handleReset = useCallback(async (): Promise<void> => {
    const confirmed = await confirm({
      content: t("chat.confirmClearHistory.description"),
      title: t("chat.confirmClearHistory.title"),
    });
    if (confirmed) {
      handleResetConversation();
    }
  }, [confirm, t, handleResetConversation]);

  const size = isCollapsed ? "calc(100% - 200px)" : "calc(100% - 65px)";

  const handleResize = useCallback((): void => {
    setIsExpanded((prev: boolean): boolean => !prev);
  }, [setIsExpanded]);

  return (
    <Container
      bgColor={theme.palette.white}
      borderLeft={
        isExpanded ? undefined : `1px solid${theme.palette.gray[300]}`
      }
      display={"flex"}
      flexDirection={"column"}
      height={isExpanded ? "calc(100% - 68px)" : "100%"}
      justify={"space-between"}
      minWidth={"420px"}
      position={isExpanded ? "fixed" : "relative"}
      right={"0px"}
      style={{
        overflowX: "hidden",
        overflowY: "auto",
        transition: "all 0.3s ease",
      }}
      top={isExpanded ? "68px" : "0px"}
      width={isExpanded ? size : "420px"}
      zIndex={2000}
    >
      <Container
        bgColor={theme.palette.white}
        display={"flex"}
        flexDirection={"column"}
        pl={1}
      >
        <Container
          alignItems={"center"}
          bgColor={theme.palette.white}
          display={"flex"}
          justify={"space-between"}
          mb={1}
          position={"sticky"}
          pr={1}
          py={1}
          top={"0px"}
          zIndex={2000}
        >
          <Icon
            icon={"sparkles"}
            iconColor={theme.palette.gray[900]}
            iconSize={"lg"}
            mr={0.25}
          />
          <Text color={theme.palette.gray[900]} size={"md"}>
            {"AI Agent"}
          </Text>
          <IconButton
            aria-label={"Reload"}
            disabled={messages.length === 0}
            icon={"history"}
            iconColor={
              messages.length === 0
                ? theme.palette.gray[400]
                : theme.palette.gray[800]
            }
            iconSize={"lg"}
            iconType={"fa-light"}
            onClick={handleReset}
            style={{ borderRadius: "50%" }}
            variant={"ghost"}
          />
          <IconButton
            aria-label={"Resize"}
            icon={
              isExpanded
                ? "arrow-down-left-and-arrow-up-right-to-center"
                : "arrow-up-right-and-arrow-down-left-from-center"
            }
            iconColor={theme.palette.gray[800]}
            iconSize={"lg"}
            iconType={"fa-light"}
            onClick={handleResize}
            style={{ borderRadius: "50%" }}
            variant={"ghost"}
          />
          <IconButton
            aria-label={"Close"}
            height={"34px"}
            icon={"xmark"}
            iconColor={theme.palette.gray[800]}
            iconSize={"lg"}
            iconType={"fa-light"}
            onClick={toggleIsOpen}
            style={{ borderRadius: "50%" }}
            variant={"ghost"}
            width={"42px"}
          />
        </Container>
        <div
          style={{
            margin: "0 auto",
            maxWidth: isExpanded ? "800px" : "100%",
            paddingRight: "20px",
            width: "100%",
          }}
        >
          <Welcome isExpanded={isExpanded} />
          {messages.length > 0 && <Messages />}
        </div>
      </Container>
      <div
        style={{
          backgroundColor: "white",
          bottom: 0,
          position: "sticky",
          width: "100%",
        }}
      >
        <InputMessage isExpanded={isExpanded} />
        <Text
          color={theme.palette.gray[400]}
          mb={1.25}
          mt={0.5}
          size={"sm"}
          textAlign={"center"}
        >
          {t("chat.footNote")}
        </Text>
      </div>
      <ConfirmClearHistory />
    </Container>
  );
};

export { Chat };
